// Şub 27, 2026 — DPD Konsolide Günlük Operasyon Raporu (Phase B)
// Mockup: konsolide_gunluk_operasyon_raporu_01092026.html
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Package, Truck, MapPin, Clock, RefreshCw, Loader2, TrendingUp,
  AlertTriangle, Award, Search, RotateCcw, PackageX, ClipboardList,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import TodayIncompleteBanner from './TodayIncompleteBanner';

const rateColor = (rate) => {
  if (rate >= 95) return 'bg-emerald-100 text-emerald-800';
  if (rate >= 90) return 'bg-green-100 text-green-800';
  if (rate >= 85) return 'bg-yellow-100 text-yellow-800';
  if (rate >= 80) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const insightIcon = (type) => {
  switch (type) {
    case 'best': return { Icon: Award, color: 'text-emerald-600 bg-emerald-50' };
    case 'review': return { Icon: AlertTriangle, color: 'text-orange-600 bg-orange-50' };
    case 'pickup': return { Icon: PackageX, color: 'text-blue-600 bg-blue-50' };
    case 'root_cause': return { Icon: Search, color: 'text-purple-600 bg-purple-50' };
    default: return { Icon: ClipboardList, color: 'text-gray-600 bg-gray-50' };
  }
};

export default function DPDConsolidatedReport() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dates, setDates] = useState([]);
  const [mode, setMode] = useState('single'); // single | range
  const [selectedDate, setSelectedDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retourPage, setRetourPage] = useState(0);
  const [retourPageSize, setRetourPageSize] = useState(50);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!firmId) return;
    api.get(`/dpd/dates?firm_id=${firmId}`).then((r) => {
      const ds = r.data?.data || [];
      setDates(ds);
      if (ds.length > 0) {
        if (!selectedDate) setSelectedDate(ds[0]);
        if (!dateTo) setDateTo(ds[0]);
        if (!dateFrom) setDateFrom(ds[Math.min(6, ds.length - 1)]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    if (mode === 'single' && !selectedDate) return;
    if (mode === 'range' && (!dateFrom || !dateTo)) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ firm_id: firmId });
      if (mode === 'single') params.set('date', selectedDate);
      else { params.set('date_from', dateFrom); params.set('date_to', dateTo); }
      params.set('retour_skip', String(retourPage * retourPageSize));
      params.set('retour_limit', String(retourPageSize));
      const r = await api.get(`/dpd/consolidated-report?${params.toString()}`);
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konsolide rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, mode, selectedDate, dateFrom, dateTo, retourPage, retourPageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Filtre değişince sayfa reset
  useEffect(() => { setRetourPage(0); }, [selectedDate, dateFrom, dateTo, firmId, mode, retourPageSize]);

  const k = data?.kpis || {};
  const tourRows = data?.tour_rows || [];
  const retourFt = data?.retour_ft || [];
  const retourFtTotal = data?.retour_ft_total || 0;
  const retourOthersTotal = data?.retour_others_total || 0;
  const isRange = data?.is_range;
  const displayPeriod = data?.display_period || selectedDate;
  const reasonBreakdown = data?.reason_breakdown || [];
  const insights = data?.insights || [];
  const retourTotalPages = Math.max(1, Math.ceil(retourFtTotal / retourPageSize));

  const setPreset = (days) => {
    if (dates.length === 0) return;
    setMode('range');
    setDateTo(dates[0]);
    setDateFrom(dates[Math.min(days - 1, dates.length - 1)]);
  };

  return (
    <div className="space-y-4" data-testid="dpd-consolidated-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-indigo-600" />
            {t('dpd.consolidated.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.consolidated.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-3">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="cons-firm-select"><SelectValue placeholder={t('dpd.common.firm')} /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="cons-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">{t('dpd.consolidated.modeSingle')}</SelectItem>
                <SelectItem value="range">{t('dpd.consolidated.modeRange')}</SelectItem>
              </SelectContent>
            </Select>
            {mode === 'single' ? (
              <>
                <Select value={selectedDate} onValueChange={setSelectedDate}>
                  <SelectTrigger data-testid="cons-date-select"><SelectValue placeholder={t('dpd.common.date')} /></SelectTrigger>
                  <SelectContent>
                    {dates.length === 0 && <div className="p-2 text-xs text-gray-500">{t('dpd.common.noData')}</div>}
                    {dates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="sm:col-span-2" />
              </>
            ) : (
              <>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="cons-date-from" />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="cons-date-to" />
                <div />
              </>
            )}
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="cons-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
          </div>

          {mode === 'range' && (
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setPreset(3)} data-testid="cons-preset-3d">{t('dpd.common.preset3')}</Button>
              <Button size="sm" variant="outline" onClick={() => setPreset(7)} data-testid="cons-preset-7d">{t('dpd.common.preset7')}</Button>
              <Button size="sm" variant="outline" onClick={() => setPreset(14)} data-testid="cons-preset-14d">{t('dpd.common.preset14')}</Button>
              <Button size="sm" variant="outline" onClick={() => setPreset(30)} data-testid="cons-preset-30d">{t('dpd.common.preset30')}</Button>
              <span className="text-xs text-gray-500 self-center ml-2">
                <CalendarIcon className="inline h-3 w-3 mr-1" />
                {displayPeriod} {k.unique_dates ? `· ${k.unique_dates} ${t('dpd.common.activeDays').toLowerCase()}` : ''}
              </span>
            </div>
          )}

          {/* Şub 27, 2026 — Bugün için veri eksikliği bilgilendirmesi.
              Tarih aralığında dateTo bugünse veya tek gün modunda dateFrom bugünse gösterir */}
          <TodayIncompleteBanner date={dateTo || dateFrom} variant="consolidated" />

          {/* KPI Kartları — mockup üstü */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard icon={Package} label={t('dpd.consolidated.kpi.parcelsPlan')} value={k.parcels_plan || 0}
                     subtitle={k.unique_dates > 1
                       ? t('dpd.consolidated.kpi.tourSumRange', { tours: k.tours || 0, days: k.unique_dates })
                       : t('dpd.consolidated.kpi.tourSum', { count: k.tours || 0 })} color="indigo" />
            <KpiCard icon={TrendingUp} label={t('dpd.consolidated.kpi.deliveredSA13')} value={k.delivered_sa13 || 0}
                     subtitle={t('dpd.consolidated.kpi.deliveryRatio', { rate: k.success_rate || 0 })} color="emerald" />
            <KpiCard icon={RotateCcw} label={t('dpd.consolidated.kpi.returnedSA23')} value={k.returned_sa23 || 0}
                     subtitle={t('dpd.consolidated.kpi.returnRate', { rate: k.return_rate || 0 })} color="red" />
            <KpiCard icon={PackageX} label={t('dpd.consolidated.kpi.pickupSA15')} value={k.pickup_sa15 || 0}
                     subtitle={t('dpd.consolidated.kpi.pickupSubtitle')} color="blue" />
            <KpiCard icon={Package} label={t('dpd.consolidated.kpi.ftRetour')} value={k.retour_scanned_ft || 0}
                     subtitle={t('dpd.consolidated.kpi.scannedOthers', { others: k.retour_scanned_others || 0 })} color="orange" />
            <KpiCard icon={MapPin} label={t('dpd.consolidated.kpi.totalKm')} value={`${k.total_km || 0} km`}
                     subtitle={t('dpd.consolidated.kpi.kmPerParcel', { v: k.km_per_parcel || 0 })} color="purple" />
            <KpiCard icon={Clock} label={t('dpd.consolidated.kpi.totalHours')} value={`${k.total_hours || 0} sa`}
                     subtitle={t('dpd.consolidated.kpi.hoursPerTour', { v: k.hours_per_tour || 0 })} color="teal" />
            <KpiCard icon={Truck} label={t('dpd.consolidated.kpi.activeLines')} value={k.tours || 0}
                     subtitle={t('dpd.consolidated.kpi.activeLinesSubtitle')} color="gray" />
          </div>

          {/* Hat & Sürücü Tablosu */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Truck className="h-4 w-4 text-indigo-600" />
              {t('dpd.consolidated.tourTableTitle')}
            </h3>
            <div className="rounded-lg border overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    {isRange && <TableHead className="w-[85px]">{t('dpd.consolidated.tourCols.date')}</TableHead>}
                    <TableHead className="w-[60px]">{t('dpd.consolidated.tourCols.line')}</TableHead>
                    <TableHead>{t('dpd.consolidated.tourCols.driverChassis')}</TableHead>
                    <TableHead className="text-right w-[70px]">{t('dpd.consolidated.tourCols.plan')}</TableHead>
                    <TableHead className="text-right w-[70px]">{t('dpd.consolidated.tourCols.sa13')}</TableHead>
                    <TableHead className="text-right w-[70px]">{t('dpd.consolidated.tourCols.sa23')}</TableHead>
                    <TableHead className="text-right w-[70px]">{t('dpd.consolidated.tourCols.sa15')}</TableHead>
                    <TableHead className="text-right w-[80px]">{t('dpd.consolidated.tourCols.distance')}</TableHead>
                    <TableHead className="w-[110px]">{t('dpd.consolidated.tourCols.durationOFD')}</TableHead>
                    <TableHead className="text-right w-[80px]">{t('dpd.consolidated.tourCols.parcelsPerHour')}</TableHead>
                    <TableHead>{t('dpd.consolidated.tourCols.endpoint')}</TableHead>
                    <TableHead className="text-right w-[90px]">{t('dpd.consolidated.tourCols.successPct')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: isRange ? 12 : 11 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!loading && tourRows.length === 0 && (
                    <TableRow><TableCell colSpan={isRange ? 12 : 11} className="text-center text-gray-500 py-8">
                      {t('dpd.consolidated.noTourData')}
                    </TableCell></TableRow>
                  )}
                  {!loading && tourRows.map((r, idx) => (
                    <TableRow key={`${r.date}-${r.tour}-${idx}`} data-testid={`cons-tour-${r.tour}`}>
                      {isRange && <TableCell className="text-[11px] font-mono text-gray-600">{r.date || '-'}</TableCell>}
                      <TableCell><Badge variant="outline" className="font-mono">{r.tour}</Badge></TableCell>
                      <TableCell>
                        <div className="font-medium">{r.courier_name || '-'}</div>
                        <div className="text-[10px] text-gray-500 truncate max-w-[180px]" title={r.chassis_nbr}>
                          Şasi: {r.chassis_nbr || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{r.parcels_plan}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-medium">{r.delivered_sa13}</TableCell>
                      <TableCell className="text-right text-red-700">{r.returned_sa23}</TableCell>
                      <TableCell className="text-right">{r.pickup_sa15}</TableCell>
                      <TableCell className="text-right">{r.distance_km} km</TableCell>
                      <TableCell>
                        <div>{r.hours} sa</div>
                        <div className="text-[10px] text-gray-500">{r.start_time || '-'} - {r.end_time || '-'}</div>
                      </TableCell>
                      <TableCell className="text-right">{r.parcels_per_hour}</TableCell>
                      <TableCell className="text-xs text-gray-600 truncate max-w-[160px]" title={r.endpoint_name}>
                        {r.endpoint_name || '-'}{r.endpoint_city ? ` (${r.endpoint_city})` : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`px-2 py-0.5 rounded font-medium ${rateColor(r.success_rate)}`}>
                          %{r.success_rate}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Retour Sebep Kodu Dağılımı */}
          {reasonBreakdown.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-red-600" />
                {t('dpd.consolidated.reasonBreakdown')}
              </h3>
              <div className="rounded-lg border overflow-hidden">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px]">{t('dpd.consolidated.reasonCols.code')}</TableHead>
                      <TableHead>{t('dpd.consolidated.reasonCols.label')}</TableHead>
                      <TableHead className="text-right w-[80px]">{t('dpd.consolidated.reasonCols.count')}</TableHead>
                      <TableHead className="text-right w-[80px]">{t('dpd.consolidated.reasonCols.pct')}</TableHead>
                      <TableHead>{t('dpd.consolidated.reasonCols.chart')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reasonBreakdown.map((x) => (
                      <TableRow key={x.code || 'unknown'} data-testid={`reason-${x.code}`}>
                        <TableCell><Badge variant="outline" className="font-mono">{x.code || '—'}</Badge></TableCell>
                        <TableCell>{x.label}</TableCell>
                        <TableCell className="text-right font-medium">{x.count}</TableCell>
                        <TableCell className="text-right">%{x.pct}</TableCell>
                        <TableCell>
                          <div className="w-full bg-gray-100 rounded-full h-2 dark:bg-gray-800">
                            <div className="bg-red-500 h-2 rounded-full" style={{ width: `${x.pct}%` }} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Retour Detay — paginated */}
          {retourFtTotal > 0 && (
            <div className="mb-6" data-testid="retour-detay-section">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <PackageX className="h-4 w-4 text-red-600" />
                  {t('dpd.consolidated.retourDetayTitle')}
                  <span className="text-xs text-gray-500 font-normal">
                    {t('dpd.consolidated.retourDetayHeader', {
                      ft: retourFtTotal.toLocaleString(),
                      othersText: retourOthersTotal > 0
                        ? t('dpd.consolidated.retourOthersText', { n: retourOthersTotal.toLocaleString() })
                        : '',
                    })}
                  </span>
                </h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500">{t('dpd.common.pageSize')}:</span>
                  <Select value={String(retourPageSize)} onValueChange={(v) => setRetourPageSize(parseInt(v, 10))}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="cons-retour-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg border overflow-x-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      {isRange && <TableHead className="w-[85px]">{t('dpd.consolidated.tourCols.date')}</TableHead>}
                      <TableHead className="w-[140px]">{t('dpd.consolidated.retourCols.packageNo')}</TableHead>
                      <TableHead className="w-[75px]">{t('dpd.consolidated.retourCols.firm')}</TableHead>
                      <TableHead className="w-[80px]">{t('dpd.consolidated.retourCols.line')}</TableHead>
                      <TableHead>{t('dpd.consolidated.retourCols.recipient')}</TableHead>
                      <TableHead className="w-[70px]">{t('dpd.consolidated.retourCols.postcode')}</TableHead>
                      <TableHead className="w-[60px] text-right">{t('dpd.consolidated.retourCols.weight')}</TableHead>
                      <TableHead>{t('dpd.consolidated.retourCols.reason')}</TableHead>
                      <TableHead className="w-[75px]">{t('dpd.consolidated.retourCols.time')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>{Array.from({ length: isRange ? 9 : 8 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                      ))}</TableRow>
                    ))}
                    {!loading && retourFt.map((p) => (
                      <TableRow key={p.id || p.pakketnummer} data-testid={`retour-row-${p.pakketnummer}`}>
                        {isRange && <TableCell className="text-[11px] font-mono text-gray-600">{p.date || '-'}</TableCell>}
                        <TableCell className="font-mono truncate max-w-[140px]" title={p.pakketnummer}>
                          {p.pakketnummer}
                        </TableCell>
                        <TableCell>
                          <Badge variant={/fast/i.test(p.subco) ? 'default' : 'secondary'} className="text-[10px]">
                            {p.subco || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">{p.tour || '-'}</span>
                        </TableCell>
                        <TableCell className="truncate max-w-[280px]" title={p.ontvanger}>
                          {p.ontvanger || '(Belirtilmemiş)'}
                        </TableCell>
                        <TableCell>{p.postcode || '-'}</TableCell>
                        <TableCell className="text-right">
                          {p.gewicht ? (p.gewicht / 1000).toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-[10px] font-mono">{p.bijkomende_code_2 || '—'}</Badge>
                            <span className="text-gray-700 truncate max-w-[220px]" title={p.reason_label}>{p.reason_label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {p.uur ? `${p.uur.slice(0, 2)}:${p.uur.slice(2, 4)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs text-gray-500">
                  {t('dpd.consolidated.shownRange', {
                    from: retourPage * retourPageSize + 1,
                    to: Math.min((retourPage + 1) * retourPageSize, retourFtTotal),
                    total: retourFtTotal.toLocaleString(),
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setRetourPage(0)} disabled={retourPage === 0 || loading} data-testid="cons-retour-first">{t('dpd.common.first')}</Button>
                  <Button size="sm" variant="outline" onClick={() => setRetourPage((p) => Math.max(0, p - 1))} disabled={retourPage === 0 || loading} data-testid="cons-retour-prev">
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="text-xs px-2">{t('dpd.common.page')} {retourPage + 1} / {retourTotalPages}</span>
                  <Button size="sm" variant="outline" onClick={() => setRetourPage((p) => Math.min(retourTotalPages - 1, p + 1))} disabled={retourPage >= retourTotalPages - 1 || loading} data-testid="cons-retour-next">
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRetourPage(retourTotalPages - 1)} disabled={retourPage >= retourTotalPages - 1 || loading} data-testid="cons-retour-last">{t('dpd.common.last')}</Button>
                </div>
              </div>
            </div>
          )}

          {/* Otomatik Yönetim Değerlendirmesi */}
          {insights.length > 0 && (
            <div className="mb-2">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Award className="h-4 w-4 text-indigo-600" />
                {t('dpd.consolidated.insightsTitle')}
              </h3>
              <div className="space-y-2">
                {insights.map((ins, i) => {
                  const { Icon, color } = insightIcon(ins.type);
                  return (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${color}`} data-testid={`insight-${ins.type}`}>
                      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <p className="text-sm leading-relaxed">{ins.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    orange: 'bg-orange-50 text-orange-800 border-orange-200',
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    gray: 'bg-gray-50 text-gray-800 border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color] || colors.gray}`}>
      <div className="flex items-center gap-2 text-xs opacity-80">
        <Icon className="h-4 w-4" />{label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{subtitle}</div>
    </div>
  );
}
